import { json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Code } from '#app/components/typography/code.tsx'
import { H2 } from '#app/components/typography/h2.tsx'
import { P } from '#app/components/typography/p.tsx'
import { Strong } from '#app/components/typography/strong.tsx'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Parts, Usage, Styling } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Strong',
}

const component: componentProps = {
	name: 'Strong',
	shortName: 'Strong',
	fileName: 'strong',
	category: 'typography',
	description: ['Strong component.'],
	features: ['Drop-in replacement for the native <strong> element.', "Opts-in to your design system's typographic styles."],
	parts: [],
	usage: `<P>
	<Strong>Warning!</Strong> This is <Strong>very dangerous</Strong>.
</P>`,
	styling: `.strong {
	@apply font-sans font-bold;
}`,
}

export const loader = async () => {
	const source = await readSource({ category: component.category, fileName: component.fileName })
	return json({ source })
}

const SpanRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, shortName, fileName, features, usage, parts, styling } = component

	return (
		<>
			<Description name={name} description={description} />
			<Usage usage={usage} shortName={shortName} />
			<P>
				<Strong>Warning!</Strong> This is <Strong>very dangerous</Strong>.
			</P>
			<P className="mt-4">
				Notice that <Code>{'<strong>'}</Code> tags will go inside <Code>{'<p>'}</Code> tags and inherit whatever typographical attributes that paragraph has.
			</P>
			<H2 className="mt-8">
				{'<b>'} vs {'<strong>'}
			</H2>
			<P>
				It is often confusing to new developers why there are so many ways to express the same thing on a rendered website. <Code>{'<b>'}</Code> and <Code>{'<strong>'}</Code> are perhaps
				one of the most common sources of confusion, causing developers to ask "Should I use <Code>{'<b>'}</Code> or <Code>{'<strong>'}</Code>? Don't they both do the same thing?"
			</P>
			<P className="mt-4">
				Not exactly. The <Code>{'<strong>'}</Code> element is for content that is of greater importance, while the <Code>{'<b>'}</Code> element is used to draw attention to text without
				indicating that it's more important.
			</P>
			<P className="mt-4">
				It may help to realize that both are valid and semantic elements in HTML and that it's a coincidence that they both have the same default styling (boldface) in most browsers
				(although some older browsers actually underline <Code>{'<strong>'}</Code>). Each element is meant to be used in certain types of scenarios, and if you want to bold text for
				decoration, you should instead actually use the CSS font-weight property.
			</P>
			<P className="mt-4">The intended meaning or purpose of the enclosed text should be what determines which element you use. Communicating meaning is what semantics are all about.</P>
			<H2 className="mt-8">
				{'<em>'} vs {'<strong>'}
			</H2>
			<P>
				Adding to the confusion is the fact that while HTML 4 defined {'<strong>'} as indicating a stronger emphasis, HTML 5 defines {'<strong>'} as representing "strong importance for
				its contents." This is an important distinction to make.
			</P>
			<P className="mt-4">
				While {'<em>'} is used to change the meaning of a sentence as spoken emphasis does ("I <em>love</em> carrots" vs. "I love <em>carrots</em>"), {'<strong>'} is used to give
				portions of a sentence added importance (e.g., "<strong>Warning!</strong> This is <strong>very dangerous</strong>.") Both {'<strong>'} and {'<em>'} can be nested to increase the
				relative degree of importance or stress emphasis, respectively.
			</P>
			<Features features={features} />
			<Parts parts={parts} />
			<P className="mt-4">The tracking's default value depends on the size prop and is controlled through CSS variables: check out the styling section.</P>

			<Source source={source} fileName={fileName} />
			<Styling styling={styling} />
		</>
	)
}
export default SpanRoute
